import { TranslateService } from '@ngx-translate/core'
import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core'
import { ContextService } from '../../../services/context.service'
import { BaseFilter } from '../../../models/BaseFilter'
import { Colors, Constants } from '../../../common/config/constants'

@Component({
  standalone: false,
  selector: 'app-mobility-widgets',
  templateUrl: './mobility-widgets.component.html',
  styleUrls: ['./mobility-widgets.component.scss'],
})
export class MobilityWidgetsComponent {
  _stats: any = null
  _statsComparison: any = null
  _filter: any = null
  _shown = true
  genderIcon = 'genders'
  variableWidgets: any = []
  targetWidgets: any = []

  colorMen = Colors.colorMen
  colorWomen = Colors.colorWomen

  tooltips = {
    nvisitors_avg_visits: {
      label: 'Avg visits per visitor',
      suffix: 'visits',
    },
    nvisitors_median_visits: {
      label: 'Median',
    },
    nvisitors_std_dev_visits: {
      label: 'Std. Deviation',
    },
    nvisits_avg_duration: {
      icon: '/assets/img/icons/positive-clear/chrono-s.svg',
      label: 'Avg visit duration',
      suffix: 'min',
    },
    nvisits_median_duration: {
      label: 'Median',
    },
    nvisits_std_dev_duration: {
      label: 'Std. Deviation',
    },
  }

  availableTargetWidgets = [
    {
      id: 'nvisits_distribution',
      nvisitors: 'Distribution of visitors by frequency of visits',
      nvisits: 'Visits distribution by visits frequency',
    },
    {
      id: 'avg_visit_duration',
      nvisitors: 'Number of visitors by average visit duration (min)',
      nvisits: "Number of visits by visitor's average visit duration (min)",
    },
  ]

  get target() {
    return this.context.vertical.delivery.target
  }

  get keyIndicator() {
    return this.context.keyIndicator.value
  }

  get keyIndicatorLabel() {
    return this.context.keyIndicator.label
  }

  get currentDelivery() {
    return this.context.vertical.delivery
  }

  @Input() legendColors = []

  @Input()
  set stats(stats) {
    this._stats = stats
    if (Object.keys(this._stats).length !== 0) {
      this.getVariablesWidgetData()
      this.getTargetWidgets()
    }
  }

  get stats() {
    return this._stats
  }

  @Input()
  set filter(filter) {
    this._filter = filter
  }

  @Input() compareMode = false
  @Input() compareLabels = []
  @Input() showLegend = true
  @Input() legendType: 'ramp' | 'colors' = 'ramp'
  @Input() legendTitle: string = null
  @Input() legendSubtitle: string = null
  @Input() hideTargetWidgets = false

  @Output() isochroneConfigChange = new EventEmitter<any>()

  constructor(
    private context: ContextService,
    private translate: TranslateService,
  ) {}

  getTerrunitTypeLabel(terrunit) {
    const region = this.context.organization.terrunit_region_id
    const labels = {}
    return labels && labels[terrunit]
  }

  getWidgetTitle(terrunit) {
    const terrunitLabel = this.translate.instant(
      this.getTerrunitTypeLabel(terrunit),
    )
    const keyLabel = this.translate.instant(`${this.keyIndicatorLabel} from`)
    return `${keyLabel} ${terrunitLabel}`
  }

  getCompValue(stats, key) {
    let res = null
    if (this.compareMode || (Array.isArray(stats) && stats.length !== 1)) {
      const st = stats.find(
        (s) =>
          s.delivery_id !== this.context.vertical.delivery.id ||
          s.query === 'q2',
      )
      res = st ? st[key] : null
    }
    return res
  }

  getValue(stats, key) {
    let res = stats[key]
    if (this.compareMode || (Array.isArray(stats) && stats.length !== 1)) {
      const st = stats.find(
        (s) =>
          ((s.delivery_id && s.delivery_id.id) || s.delivery_id) ===
            this.context.vertical.delivery.id || s.query === 'q1',
      )
      res = st ? st[key] : null
    }
    return res
  }

  getVariablesWidgetData() {
    if (
      !this._stats ||
      Object.keys(this._stats).length === 0 ||
      !this._stats.poiStats
    ) {
      return []
    }
    const transform = (stats) =>
      Object.keys(this.currentDelivery.config.variables)
        .filter((key) => key !== this.context.keyIndicator.value)
        .map((key) => ({
          compValue: this.getCompValue(stats, key),
          value: this.getValue(stats, key),
          label: this.translate.instant(
            `Number of ${
              (this.context.keyIndicatorLabels[key] &&
                this.context.keyIndicatorLabels[key].toLowerCase()) ||
              key
            }`,
          ),
        }))
        .filter((item) => item.value !== undefined)
    this.variableWidgets = transform(this._stats.poiStats)
  }

  getTooltipWidgetData() {
    if (
      !this._stats ||
      Object.keys(this._stats).length === 0 ||
      !this._stats.poiStats ||
      !this.context.isEntirePopulationActive()
    ) {
      return []
    }
    const transform = (stats) =>
      this.currentDelivery.tooltip
        .filter((t) => this.tooltips[t.value])
        .map((t) => {
          const key = t.value
          return {
            ...this.tooltips[key],
            compValue: this.getCompValue(stats, key),
            value: this.getValue(stats, key),
          }
        })
        .filter((item) => item.value !== undefined)
    return transform(this._stats.poiStats)
  }

  getLegendTitle() {
    if (this.legendTitle) {
      return this.legendTitle
    }

    let prefix =
      this.compareMode && !this.context.vertical.isOOH()
        ? 'Delta of'
        : 'Number of'
    let unit = ''
    const keyIndicator = this.context.keyIndicator.label.toLowerCase()
    if (this.context.isDensityActive()) {
      prefix = this.compareMode ? 'Density delta of' : 'Density of'
      unit = `(${this.translate.instant(keyIndicator)}/km²)`
    }
    const title = this.translate.instant(`${prefix} ${keyIndicator}`)
    return title + (unit ? ' ' + unit : unit)
  }

  getLegendExtra() {
    return this.stats && this.stats.legendExtra
  }

  isKeyIndicator(label) {
    return this.keyIndicator !== label
  }

  canShowTripSummary() {
    return (
      this.canShowStatsWidget('tripsSummary') &&
      this.context.vertical.delivery.config.showVisitorsFrom
    )
  }

  canShowTargetWidget(widgetName: string) {
    if (this.hideTargetWidgets) return false
    return (
      this.isTargetAvailable(widgetName) &&
      this.getTargetWidgetData(widgetName).length > 0
    )
  }

  canShowStatsWidget(widgetName: string) {
    return (
      this._stats &&
      this._stats[widgetName] &&
      this.getWidgetData(widgetName).length
    )
  }

  getWidgetData(widgetName: string) {
    return this._stats && (this._stats[widgetName] || [])
  }

  getWidgetUnit(widgetName: string, _default = '') {
    const data = this.getWidgetData(widgetName)
    return data.length ? data[0].unit || _default : _default
  }

  getWidgetValue(widgetName: string) {
    if (this._filter.target && this._filter.target[widgetName]) {
      const value =
        this._filter.target[widgetName] instanceof Array
          ? this._filter.target[widgetName]
          : [this._filter.target[widgetName]]

      value[0] === 'nvisitors_gender_m'
        ? (this.genderIcon = 'genders_men')
        : (this.genderIcon = 'genders_women')

      return value
    } else {
      this.genderIcon = 'genders'
      return []
    }
  }

  getTargetWidgets() {
    this.targetWidgets = this.availableTargetWidgets.map((widget) => ({
      ...widget,
      label: widget[this.keyIndicator],
    }))
  }

  getTargetWidgetData(widgetName) {
    return (
      (this._stats &&
        this._stats.targetStats &&
        this._stats.targetStats[widgetName]) ||
      []
    )
  }

  getTargetWidgetUnit(widgetName, _default = '') {
    const data = this.getTargetWidgetData(widgetName)
    return data.length ? data[0].unit || _default : _default
  }

  isTargetAvailable(targetName) {
    return (
      !this.hasTargetSelected() ||
      this.isTargetSelected(targetName) !== undefined
    )
  }

  isTargetSelected(target) {
    return this.target.subfilters.find(
      (filter: BaseFilter) =>
        filter.id === target && filter.value !== undefined,
    )
  }

  hasTargetSelected() {
    return (
      this.target instanceof BaseFilter &&
      this.target.subfilters.some(
        (filter: BaseFilter) => filter.value !== undefined,
      )
    )
  }

  getLegendSubtitle() {
    if (this.legendSubtitle) {
      return this.legendSubtitle
    }

    if (this.context.vertical.isOOH()) {
      return this.keyIndicator === 'nvisitors'
        ? `The scope of the campaign is defined as the number of impacts of advertising panels to different people (unique impacts)`
        : `The scope of the campaign is defined as the number of impacts of advertising panels, regardless of the repetition of people (more than one impact per person)`
    } else {
      return this.context.isDensityActive()
        ? this.keyIndicator === 'nvisitors'
          ? `Visitors' density is calculated as the number of visitors over/divided by the selected area`
          : `Visits' density is calculated as the number of visits over/divided by the selected area`
        : this.keyIndicator === 'nvisitors'
        ? 'A visitor will be considered someone whose presence has been detected in a neighbourhood'
        : 'A visit will be considered each presence of a visitor that has been detected in a neighbourhood which is not his/her residence'
    }
  }

  isDensityAvailable() {
    return this.currentDelivery.config.density
  }
}
